<script setup>
import { getPassengersText, getTicketClassText } from '@js/utils/flight';
import { l, lreplace } from '@utils/toolbox';
import { onMounted, ref } from 'vue';
import { useChatApi } from './useChatApi';

const props = defineProps({
	content: {
		type: Object,
		required: true
	}
});

const { resolveIatas } = useChatApi(false);

function formatPassengers (passengers = {}) {
	let passengersData = passengers;

	if (passengers.type === 'youth') {
		passengersData = {
			youth: {
				count: passengers.ages.length,
				ages: passengers.ages
			}
		};
	} else {
		passengersData = {
			adults: passengers.adults || 1,
			child: {
				count: passengers?.childrenAges?.length || 0,
				ages: passengers?.childrenAges || []
			}
		};
	}


	return getPassengersText(passengersData, passengers.type === 'youth');
}

function formatOpenJawPassengers ({ passengers, adults = 1, childrenAges = [] } = {}) {
	if (passengers) {
		return formatPassengers(passengers);
	}

	return getPassengersText({
		adults,
		children: childrenAges.length
			? { count: childrenAges.length, ages: childrenAges }
			: undefined
	});
}

function getResolvedLocationText (location) {
	if (!location || typeof location !== 'object') {
		return null;
	}
	if (location.name && location.country) {
		return `${location.name}, ${location.country}`;
	}
	if (location.name) {
		return location.name;
	}
	return null;
}

async function summarizeFlightSearch ({
	from,
	to,
	departureDate,
	returnDate,
	passengers,
	ticketClass = 0
} = {}) {
	const passengersText = formatPassengers(passengers);
	const route = `${await resolveIatas(from)} ${l('global.simplePhrases.To').toLowerCase()} ${await resolveIatas(to)}`;
	const type = returnDate ? l('searchBox.flightType.twoWay') : l('searchBox.flightType.oneWay');
	const date = returnDate ? `${departureDate} – ${returnDate}` : departureDate;

	return lreplace('chat.search.summary', {
		route,
		date,
		type,
		passengers: passengersText,
		class: getTicketClassText(ticketClass)
	});
}

async function summarizeOpenJawSearch ({ legs = [], ticketClass = 0, ...rest } = {}) {
	if (!Array.isArray(legs) || legs.length === 0) {
		return l('chat.search.openJaw.started');
	}

	const passengersText = formatOpenJawPassengers(rest);

	const legTexts = await Promise.all(
		legs.map(async (leg) => {
			let from = leg.from;
			let to = leg.to;

			if (typeof from?.[0] === 'string') {
				from = await resolveIatas(leg.from);
				to = await resolveIatas(leg.to);
			} else {
				from = getResolvedLocationText(leg.from[0]) || from;
				to = getResolvedLocationText(leg.to[0]) || to;
			}

			return lreplace('chat.search.openJaw.leg', { from, to, date: leg.date });
		})
	);

	return lreplace('chat.search.summary.openJaw', {
		type: l('searchBox.flightType.openJawShort'),
		legs: legTexts.join(' · '),
		passengers: passengersText,
		class: getTicketClassText(ticketClass)
	});
}

const text = ref(null);

onMounted(async () => {
	const { name, arguments: args } = props.content || {};

	if (['searchFlightsOneWay', 'searchFlightsReturn'].includes(name)) {
		text.value = await summarizeFlightSearch(args);
	} else if (name === 'searchFlightsOpenJaw') {
		text.value = await summarizeOpenJawSearch(args);
	}
});
</script>

<template>
  <div
    v-if="text"
    class="message-bubble assistant flight-search-status"
  >
    <div class="message-content">
      <div
        class="flight-search-started"
      >
        {{ text }}
      </div>
    </div>
  </div>
</template>


<style>
.flight-search-status {
  width: fit-content;
}
</style>
